/**
 * EISettings — Settings tab with sub-sections.
 *
 * - Analysis Catalogue   : What every DISC report unlocks (read-only)
 * - Question Backlog     : Suggested future questions / better wording / observations (CRUD)
 */

import { useEffect, useState } from "react";
import axios from "axios";
import {
  ListChecks, BookOpenCheck, Plus, Loader2, Trash2, Check, Archive, RotateCcw, X,
} from "lucide-react";
import { API, toast } from "../EmploymentIntelligence";

const SUB_TABS = [
  { key: "catalogue", label: "Analysis Catalogue", Icon: BookOpenCheck },
  { key: "backlog",   label: "Question Backlog",   Icon: ListChecks },
];

export default function EISettings() {
  const [tab, setTab] = useState("catalogue");
  return (
    <>
      <div className="ei-section-head">
        <div>
          <h2>Settings</h2>
          <p>Admin controls, analysis reference and question backlog for ongoing improvement.</p>
        </div>
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
        {SUB_TABS.map(({ key, label, Icon }) => (
          <button
            key={key}
            className={`ei-tab ${tab === key ? "is-active" : ""}`}
            onClick={() => setTab(key)}
            data-testid={`ei-settings-tab-${key}`}
          >
            <Icon size={13} /> {label}
          </button>
        ))}
      </div>

      {tab === "catalogue" && <AnalysisCatalogue />}
      {tab === "backlog" && <QuestionBacklog />}
    </>
  );
}

/* ─────────────────────────────────────────────────────────
   Analysis Catalogue — what we can do with the DISC data
   ───────────────────────────────────────────────────────── */
const CATALOGUE = [
  {
    title: "Per-employee signals",
    blurb: "What every uploaded assessment unlocks for a single profile.",
    items: [
      {
        head: "Core DISC scores (0-100)",
        body: "Dominance, Influence, Steadiness, Compliance — the raw inputs for everything downstream.",
      },
      {
        head: "Primary + secondary style + blend",
        body: "12 distinct two-letter blends (Di, Is, Cs, etc.), each with its own strength/blind-spot patterns.",
      },
      {
        head: "Score spread",
        body: "Range between highest and lowest score. >40 = strongly defined profile, <20 = adaptive everyman profile.",
      },
      {
        head: "Adapted vs Natural gap",
        body: "Gap above 30pt on any axis = burnout / role-stretch risk. They're performing a personality.",
      },
      {
        head: "Behavioural narrative (AI)",
        body: "Personality summary · communication · leadership · motivators · stress triggers · strengths · development areas · ideal environment.",
      },
    ],
  },
  {
    title: "Workforce-wide analyses",
    blurb: "Once 10+ assessments exist these dashboards become diagnostic.",
    items: [
      {
        head: "Primary-style distribution",
        body: "% of D / I / S / C across the company. Spot dominant cultures or skill gaps.",
      },
      {
        head: "Blend distribution",
        body: "How many people sit in each of the 12 two-letter blends.",
      },
      {
        head: "Department comparison heatmap",
        body: "Which department over/under-indexes on which trait.",
      },
      {
        head: "Score-spread distribution",
        body: "% of workforce with sharp vs plateau profiles. Predicts adaptability.",
      },
    ],
  },
  {
    title: "Retention / Attrition analytics",
    star: true,
    blurb: "The big one. Once status='former' profiles exist, the engine answers 'why do people leave?'",
    items: [
      { head: "Style-specific churn rate", body: "Which DISC styles are over-indexed in leavers vs current workforce." },
      { head: "High-D + Low-S pattern", body: "Classic high-drive + low-patience attrition signal, surfaced with confidence score." },
      { head: "Tenure by style + by blend", body: "Average months retained per primary style and per 2-letter blend." },
      { head: "Department × style heatmap", body: "Which (dept, style) combos burn out fastest." },
      { head: "Manager retention scoring", body: "Managers ranked by team tenure — surfaces who keeps people." },
      { head: "Score-gap leavers", body: "Employees with >30pt adapted/natural gap weighted heavier in attrition predictions." },
      { head: "Attrition Risk Score (per employee + per team)", body: "Low / Medium / High flag based on all the above + tenure curve." },
    ],
  },
  {
    title: "Top-performer DNA",
    blurb: "When employees are tagged High Performer / Top Talent / Promotion, this engine learns what success looks like.",
    items: [
      { head: "Average DISC of top performers vs company avg", body: "The shape of what 'good' looks like." },
      { head: "Top-performer blend ranking", body: "Which 2-letter blends win most often." },
      { head: "Score thresholds", body: "E.g. 'all Photography top performers have I ≥ 60' — auto-detected rules." },
      { head: "Differential traits", body: "What top performers share that average performers don't." },
    ],
  },
  {
    title: "Recommended DISC Ranges by Role",
    blurb: "Auto-learnt role templates. Activate once 3+ employees exist in a role.",
    items: [
      { head: "P25–P75 ranges per axis per role", body: "The shape of 'good' for each job, learnt from your real hires." },
      { head: "Confidence-by-sample-size", body: "More employees in a role = tighter, more confident range." },
      { head: "Auto-updates", body: "Range refreshes every time a new assessment lands." },
    ],
  },
  {
    title: "Team analytics",
    blurb: "Per-department dashboards.",
    items: [
      { head: "Team Balance Score", body: "How varied the team's primary styles are vs the ideal balance for the team's function." },
      { head: "Behavioural Diversity Index", body: "Simpson's diversity index applied to blends across the team." },
      { head: "Leadership distribution", body: "Count of D-primary or Dc / Di blends — where leadership lives." },
      { head: "Communication profile", body: "Dominant communication mode across the team." },
      { head: "Team strengths and gaps", body: "E.g. 'this team has no C-primary members — detail-work risk'." },
    ],
  },
  {
    title: "Hiring intelligence",
    blurb: "Decision support for open roles.",
    items: [
      { head: "Recommended DISC for an open role", body: "Based on top-performer DNA + retention data." },
      { head: "Gap analysis", body: "Which styles is the company short on right now." },
      { head: "Diversity warnings", body: "Flag if all shortlisted candidates look identical." },
    ],
  },
  {
    title: "Comparison Centre",
    blurb: "Side-by-side delta on every score and behavioural area.",
    items: [
      { head: "Employee vs employee", body: "Radar overlay + difference table." },
      { head: "Employee vs company / department / role-template", body: "How they sit against any baseline." },
      { head: "Department vs department", body: "Which team has the wider spread, which has the bench depth." },
    ],
  },
  {
    title: "AI Insights engine",
    blurb: "On-demand auto-discoveries. Each insight has confidence + supporting data + sample size.",
    items: [
      { head: "Style × tenure correlations", body: "'D ≥ 70 employees retain 38% shorter on average. n=24.'" },
      { head: "Blend × promotion patterns", body: "'Di blends represent 6 of 9 promotions in the last 18 months.'" },
      { head: "Manager × retention spotlights", body: "'Photography under Jonathan: 92% retention vs company 71%.'" },
      { head: "Role × style mismatches", body: "'3 customer-service hires with D ≥ 65 left within 9 months.'" },
      { head: "Diversity / succession gaps", body: "'Marketing 100% I-primary, no C-primary — content QA gap likely.'" },
      { head: "Burnout signals", body: "'4 active employees have >30pt adapted/natural gaps on Steadiness.'" },
    ],
  },
  {
    title: "Natural-language assistant",
    blurb: "ChatGPT-style panel grounded in your actual employee data. Every answer cites sample size.",
    items: [
      { head: "Example questions", body: "Which DISC type leaves most often? · What traits do top performers share? · Which department is most balanced? · Who's at attrition risk right now? · Show me everyone with C ≥ 70." },
    ],
  },
  {
    title: "Reports (PDF / Excel / CSV)",
    blurb: "Executive-ready exports for board and HR.",
    items: [
      { head: "Available templates", body: "Executive Summary · Department Report · Recruitment Report · Retention Report · Leadership Report · Former Employee Analysis · Workforce Personality Analysis." },
    ],
  },
];

function AnalysisCatalogue() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div className="ei-card">
        <p style={{ margin: 0, color: "#4a5060", fontSize: 13.5, lineHeight: 1.6 }}>
          Every signal the platform can pull from a DISC assessment, plus every analysis available once you have a population
          of profiles to compare. Phase 1 ships the foundation (PDF ingestion, behavioural narrative, basic dashboards).
          Phase 2 layers retention prediction, top-performer DNA, the AI Insights engine and the natural-language assistant on top.
        </p>
      </div>

      {CATALOGUE.map((section) => (
        <section key={section.title} className="ei-card">
          <div className="ei-card-head">
            <h3 className="ei-card-title">
              {section.star && <span style={{ color: "#f59e0b", marginRight: 4 }}>★</span>}
              {section.title}
            </h3>
            <span className="ei-card-sub">{section.blurb}</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12 }}>
            {section.items.map((item, i) => (
              <div
                key={i}
                style={{
                  background: "#fafbf6",
                  border: "1px solid #ebede4",
                  borderRadius: 12,
                  padding: "14px 16px",
                }}
              >
                <div style={{ fontSize: 12.5, fontWeight: 800, color: "#0d1b2a", marginBottom: 4 }}>
                  {item.head}
                </div>
                <div style={{ fontSize: 12.5, color: "#4a5060", lineHeight: 1.5 }}>
                  {item.body}
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}


/* ─────────────────────────────────────────────────────────
   Question Backlog — CRUD list of suggestions
   ───────────────────────────────────────────────────────── */

const STATUS_LABELS = {
  open:        { label: "Open",        color: "#1e40af", bg: "rgba(59, 130, 246, 0.14)" },
  in_progress: { label: "In progress", color: "#92400e", bg: "rgba(245, 158, 11, 0.16)" },
  shipped:     { label: "Shipped",     color: "#047857", bg: "rgba(16, 185, 129, 0.16)" },
  archived:    { label: "Archived",    color: "rgba(255,255,255,0.65)", bg: "#f1f2ec" },
};

const CATEGORIES = [
  { key: "question",    label: "Better DISC question" },
  { key: "analysis",    label: "Analysis idea" },
  { key: "observation", label: "Observation" },
  { key: "other",       label: "Other" },
];

function QuestionBacklog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [draft, setDraft] = useState({ title: "", body: "", category: "question", tags: "" });
  const [filter, setFilter] = useState("open");  // open | in_progress | shipped | archived | all

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = filter === "all" ? {} : { status: filter };
        const res = await axios.get(`${API}/employment-intelligence/backlog`, { params });
        if (!cancelled) setItems(res.data || []);
      } catch {
        if (!cancelled) toast.error("Could not load backlog");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [filter]);

  const refresh = async () => {
    const params = filter === "all" ? {} : { status: filter };
    const res = await axios.get(`${API}/employment-intelligence/backlog`, { params });
    setItems(res.data || []);
  };

  const handleCreate = async () => {
    if (!draft.title.trim()) {
      toast.error("Add a title first");
      return;
    }
    try {
      await axios.post(`${API}/employment-intelligence/backlog`, {
        title: draft.title.trim(),
        body: draft.body.trim(),
        category: draft.category,
        tags: draft.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      setShowNew(false);
      setDraft({ title: "", body: "", category: "question", tags: "" });
      toast.success("Added to backlog");
      refresh();
    } catch {
      toast.error("Could not add item");
    }
  };

  const handleSetStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/employment-intelligence/backlog/${id}`, { status });
      refresh();
    } catch { toast.error("Could not update"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this item permanently?")) return;
    try {
      await axios.delete(`${API}/employment-intelligence/backlog/${id}`);
      toast.success("Deleted");
      refresh();
    } catch { toast.error("Could not delete"); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="ei-card">
        <div className="ei-card-head">
          <h3 className="ei-card-title">Question Backlog</h3>
          <div style={{ display: "flex", gap: 6 }}>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="ei-select"
            >
              <option value="open">Open</option>
              <option value="in_progress">In progress</option>
              <option value="shipped">Shipped</option>
              <option value="archived">Archived</option>
              <option value="all">All</option>
            </select>
            <button className="ei-primary" onClick={() => setShowNew(true)} data-testid="ei-backlog-add">
              <Plus size={13} /> Add idea
            </button>
          </div>
        </div>
        <p style={{ margin: 0, color: "#4a5060", fontSize: 13, lineHeight: 1.6 }}>
          Capture future DISC questions, better wording for existing ones, analysis ideas, and observations
          to revisit. Tag and triage them later — they stay tied to the Employment Intelligence module.
        </p>
      </div>

      {showNew && (
        <div className="ei-card" data-testid="ei-backlog-form">
          <div className="ei-card-head">
            <h3 className="ei-card-title">New idea</h3>
            <button className="ei-secondary" onClick={() => setShowNew(false)}><X size={13} /> Cancel</button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: "rgba(255,255,255,0.65)" }}>
                Title *
              </span>
              <input
                type="text"
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                placeholder="e.g. Add a follow-up: 'Why did you choose this answer?'"
                data-testid="ei-backlog-title"
                style={{ border: "1px solid #d8dcd4", borderRadius: 8, padding: "9px 12px", fontSize: 13.5, fontFamily: "inherit" }}
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: "rgba(255,255,255,0.65)" }}>
                Detail (optional)
              </span>
              <textarea
                rows={3}
                value={draft.body}
                onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                placeholder="What's the idea? Why does it matter?"
                data-testid="ei-backlog-body"
                style={{ border: "1px solid #d8dcd4", borderRadius: 8, padding: "9px 12px", fontSize: 13.5, fontFamily: "inherit" }}
              />
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: "rgba(255,255,255,0.65)" }}>
                  Category
                </span>
                <select
                  className="ei-select"
                  value={draft.category}
                  onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                >
                  {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: "rgba(255,255,255,0.65)" }}>
                  Tags (comma-separated)
                </span>
                <input
                  type="text"
                  value={draft.tags}
                  onChange={(e) => setDraft({ ...draft, tags: e.target.value })}
                  placeholder="retention, photography, hiring"
                  style={{ border: "1px solid #d8dcd4", borderRadius: 8, padding: "9px 12px", fontSize: 13.5, fontFamily: "inherit" }}
                />
              </label>
            </div>
            <div>
              <button className="ei-primary" onClick={handleCreate} data-testid="ei-backlog-save">
                <Plus size={13} /> Add to backlog
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 30, textAlign: "center", color: "rgba(255,255,255,0.65)" }}>
          <Loader2 size={18} className="ei-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="ei-empty">
          <h3>No items in this view</h3>
          <p>Click &ldquo;Add idea&rdquo; to log a new question, analysis or observation.</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {items.map((item) => {
            const status = STATUS_LABELS[item.status] || STATUS_LABELS.open;
            const cat = CATEGORIES.find((c) => c.key === item.category)?.label || "Other";
            return (
              <div key={item.id} className="ei-card" data-testid={`ei-backlog-item-${item.id}`}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4, flexWrap: "wrap" }}>
                      <span
                        style={{
                          padding: "3px 9px",
                          borderRadius: 999,
                          fontSize: 10.5,
                          fontWeight: 800,
                          textTransform: "uppercase",
                          letterSpacing: 0.6,
                          background: status.bg,
                          color: status.color,
                        }}
                      >
                        {status.label}
                      </span>
                      <span style={{ fontSize: 11, color: "rgba(255,255,255,0.65)", fontWeight: 600 }}>{cat}</span>
                      {(item.tags || []).map((t, i) => (
                        <span
                          key={i}
                          style={{
                            padding: "2px 8px",
                            borderRadius: 999,
                            background: "#f1f2ec",
                            color: "#4a5060",
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#0d1b2a", marginBottom: 4 }}>
                      {item.title}
                    </div>
                    {item.body && (
                      <div style={{ fontSize: 12.5, color: "#4a5060", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                        {item.body}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 }}>
                    {item.status !== "shipped" && (
                      <button
                        className="ei-secondary"
                        onClick={() => handleSetStatus(item.id, "shipped")}
                        title="Mark shipped"
                        data-testid={`ei-backlog-ship-${item.id}`}
                      >
                        <Check size={13} /> Ship
                      </button>
                    )}
                    {item.status !== "in_progress" && item.status !== "shipped" && (
                      <button
                        className="ei-secondary"
                        onClick={() => handleSetStatus(item.id, "in_progress")}
                        title="Mark in progress"
                      >
                        <RotateCcw size={13} /> Pick up
                      </button>
                    )}
                    {item.status !== "archived" && (
                      <button
                        className="ei-secondary"
                        onClick={() => handleSetStatus(item.id, "archived")}
                        title="Archive"
                      >
                        <Archive size={13} /> Archive
                      </button>
                    )}
                    <button
                      className="ei-secondary"
                      onClick={() => handleDelete(item.id)}
                      style={{ color: "#9f1239", borderColor: "#fecdd3" }}
                    >
                      <Trash2 size={13} /> Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
